/**
 * Single source of truth for the time-of-day theme.
 *
 * The site auto-picks light during the day and dark at night. A manual pick from
 * the theme selector wins, but only for the calendar day it was made on — hence
 * the `theme-date` stamp next to the stored theme.
 *
 * The writer (theme selector) and the readers (ThemeController + the pre-paint
 * script below) MUST derive that date the same way. They used to disagree — the
 * selector stamped a UTC date while the controller compared against a local one
 * — so every evening west of Greenwich the override could never match, and the
 * 60s tick wiped it and snapped the theme back. Keep both on `getLocalDateString`.
 */

export const THEME_KEY = 'theme';
export const THEME_DATE_KEY = 'theme-date';

/**
 * Local calendar date as YYYY-MM-DD. Never use `toISOString()` here: that yields
 * the UTC date, which runs ahead of the local one for the whole evening in the
 * Americas (e.g. 21:00 in Toronto is already tomorrow in UTC).
 */
export function getLocalDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getTimeBasedTheme(date: Date = new Date()): 'light' | 'dark' {
  const hour = date.getHours();
  return hour >= 6 && hour < 18 ? 'light' : 'dark';
}

/**
 * Runs in <head>, ahead of next-themes' own inline script: it resolves the
 * time-based theme and seeds localStorage so next-themes paints the right theme
 * on the first frame. Without it next-themes paints `defaultTheme` ("dark") and
 * ThemeController corrects it from an effect — a visible dark→light flash on
 * every load during daylight hours.
 *
 * Mirrors getLocalDateString/getTimeBasedTheme; keep the three in step.
 */
export const THEME_INIT_SCRIPT = `(function(){try{
var n=new Date(),p=function(v){return(v<10?'0':'')+v};
var today=n.getFullYear()+'-'+p(n.getMonth()+1)+'-'+p(n.getDate());
if(localStorage.getItem('${THEME_DATE_KEY}')===today)return;
localStorage.removeItem('${THEME_DATE_KEY}');
var h=n.getHours();
localStorage.setItem('${THEME_KEY}',h>=6&&h<18?'light':'dark');
}catch(e){}})();`;

/**
 * Inside the Chrome extension's side panel the site is always dark, so it reads
 * as one piece with the panel around it. Rendered as the first child of
 * next-themes' provider, so it runs right after next-themes' own pre-paint
 * script and overrides the class that script set.
 *
 * It writes nothing: a page framed by the extension shares localStorage with the
 * site opened normally, so a stored theme would turn the visitor's ordinary
 * visits dark too. ThemeProvider keeps it dark after hydration (`forcedTheme`).
 */
export const FRAMED_DARK_SCRIPT = `(function(){try{
var f;try{f=window.self!==window.top}catch(e){f=true}
if(!f)return;
var d=document.documentElement;
d.classList.remove('light');d.classList.add('dark');d.style.colorScheme='dark';
}catch(e){}})();`;
